import { useState, useCallback, forwardRef, useImperativeHandle } from "react";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { 
  Brain, 
  Loader2, 
  PlusCircle, 
  CheckCircle, 
  AlertTriangle, 
  BookOpen,
  Sparkles,
  Copy,
  RotateCcw,
  FileText,
  X,
  Check,
  Clock
} from "lucide-react";
import CustomPromptInput from "./CustomPromptInput";
import { RevisionHistory, type RevisionEntry } from "./RevisionHistory";
import { RevisionComparisonModal } from "./RevisionComparisonModal";
import { ContentHealthScorer } from "./ContentHealthScorer";
import { AutoFormatter } from "./AutoFormatter";
import { RealtimeSuggestions } from "./RealtimeSuggestions";
import { RevisionTemplates } from "./RevisionTemplates";

interface NoteAIAssistantProps {
  content: string;
  title: string;
  topic?: string;
  onContentUpdate: (content: string) => void;
}

type AssistantAction = 'expand' | 'improve' | 'fact_check' | 'suggest_terms' | 'generate' | 'smart_revision' | 'content_health';

interface ActionButton {
  action: AssistantAction;
  label: string;
  icon: React.ComponentType<any>;
  description: string;
  variant?: "default" | "outline" | "secondary";
}

const actions: ActionButton[] = [
  {
    action: 'smart_revision',
    label: 'Smart Revision',
    icon: Sparkles,
    description: 'Comprehensive review with side-by-side comparison',
    variant: 'default'
  },
  {
    action: 'content_health',
    label: 'Content Health',
    icon: CheckCircle,
    description: 'Analyze content quality metrics',
    variant: 'secondary'
  },
  {
    action: 'expand',
    label: 'Expand Content',
    icon: PlusCircle,
    description: 'Add more detailed medical information and explanations',
    variant: 'outline'
  },
  {
    action: 'improve',
    label: 'Improve Writing',
    icon: Sparkles,
    description: 'Enhance clarity, organization, and readability',
    variant: 'outline'
  },
  {
    action: 'fact_check',
    label: 'Fact Check',
    icon: AlertTriangle,
    description: 'Verify medical accuracy and identify potential issues',
    variant: 'outline'
  },
  {
    action: 'suggest_terms',
    label: 'Suggest Terms',
    icon: BookOpen,
    description: 'Get relevant medical terminology suggestions',
    variant: 'outline'
  }
];

const NoteAIAssistant = forwardRef<{ triggerAction: (action: AssistantAction) => void }, NoteAIAssistantProps>(
  ({ content, title, topic, onContentUpdate }, ref) => {
  const [assistantResult, setAssistantResult] = useState("");
  const [currentAction, setCurrentAction] = useState<AssistantAction | null>(null);
  const [originalContent, setOriginalContent] = useState("");
  const [customPrompt, setCustomPrompt] = useState("");
  const [showComparison, setShowComparison] = useState(false);
  const [healthScore, setHealthScore] = useState<any>(null);
  const [revisionHistory, setRevisionHistory] = useState<RevisionEntry[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [realtimeSuggestionsEnabled, setRealtimeSuggestionsEnabled] = useState(false);

  const assistantMutation = useMutation({
    mutationFn: async (action: AssistantAction) => {
      const { data, error } = await supabase.functions.invoke('ai-note-assistant', {
        body: {
          action,
          content,
          title,
          topic,
          context: action === 'generate' ? 'Educational note creation' : undefined,
          customPrompt: customPrompt.trim() || undefined
        }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      if (data.healthScore) {
        setHealthScore(data.healthScore);
        setAssistantResult("");
        setCurrentAction('content_health');
      } else {
        setAssistantResult(data.result);
        setCurrentAction(data.action);
        setHealthScore(null);
        if (data.action === 'smart_revision') {
          setShowComparison(true);
        }
        if (data.action !== 'fact_check' && data.action !== 'suggest_terms') {
          setOriginalContent(content);
        }
      }
      toast.success(`${data.action} completed! Used ${data.tokensUsed} tokens ($${data.estimatedCost.toFixed(4)})`);
    },
    onError: (error) => {
      console.error('AI Assistant error:', error);
      toast.error('Failed to process request');
    }
  });

  const handleAction = (action: AssistantAction) => {
    if (!content.trim() && action !== 'generate') {
      toast.error('Please add some content first');
      return;
    }
    assistantMutation.mutate(action);
  };

  const handleApplyResult = useCallback(() => {
    if (assistantResult && (currentAction === 'expand' || currentAction === 'improve' || currentAction === 'generate' || currentAction === 'smart_revision')) {
      // Save to revision history before applying
      const newRevision: RevisionEntry = {
        id: Date.now().toString(),
        content: assistantResult,
        action: currentAction,
        timestamp: new Date(),
      };
      setRevisionHistory(prev => [newRevision, ...prev]);
      
      onContentUpdate(assistantResult);
      toast.success('Content updated successfully');
      setAssistantResult("");
      setCurrentAction(null);
    }
  }, [assistantResult, currentAction, onContentUpdate]);

  const handleCopyResult = () => {
    navigator.clipboard.writeText(assistantResult);
    toast.success('Result copied to clipboard');
  };

  const handleRevert = useCallback(() => {
    if (originalContent) {
      onContentUpdate(originalContent);
      toast.success('Reverted to original content');
    }
    setAssistantResult("");
    setCurrentAction(null);
    setOriginalContent("");
    setHealthScore(null);
    setShowComparison(false);
  }, [originalContent, onContentUpdate]);

  const handleRevertToRevision = useCallback((content: string) => {
    onContentUpdate(content);
    toast.success('Reverted to selected revision');
    setShowHistory(false);
  }, [onContentUpdate]);

  const isLoading = assistantMutation.isPending;

  const toggleHistory = () => setShowHistory(!showHistory);

  // Expose triggerAction to parent via ref
  useImperativeHandle(ref, () => ({
    triggerAction: handleAction,
  }));

  return (
    <Card className="border-primary/20">
      <CardHeader>
        <CardTitle className="flex items-center justify-between text-lg">
          <span className="flex items-center gap-2">
            <Brain className="h-5 w-5 text-primary" />
            AI Writing Assistant
          </span>
          {revisionHistory.length > 0 && (
            <Button 
              size="sm" 
              variant="outline"
              onClick={toggleHistory}
              className="text-xs"
            >
              <Clock className="h-3 w-3 mr-1" />
              History ({revisionHistory.length})
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Action Buttons */}
        <div className="grid grid-cols-2 gap-2">
          {actions.map(({ action, label, icon: Icon, description, variant = 'outline' }) => (
            <Button
              key={action}
              variant={variant}
              size="sm"
              onClick={() => handleAction(action)}
              disabled={isLoading}
              className="flex items-center gap-2 h-auto p-3 flex-col"
              title={description}
            >
              <Icon className="h-4 w-4" />
              <span className="text-xs">{label}</span>
            </Button>
          ))}
        </div>

        {/* Generate Button (Special case for empty content) */}
        {!content.trim() && (
          <Button
            onClick={() => handleAction('generate')}
            disabled={isLoading}
            className="w-full flex items-center gap-2"
            variant="default"
          >
            <Sparkles className="h-4 w-4" />
            Generate Content from Title
          </Button>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>AI is working...</span>
          </div>
        )}

        {/* Health Score Display */}
        {healthScore && (
          <ContentHealthScorer
            score={healthScore}
            onClose={() => {
              setHealthScore(null);
              setCurrentAction(null);
            }}
          />
        )}

        {/* Results Display */}
        {assistantResult && !healthScore && (
          <div className="space-y-3 border-t pt-4">
            <div className="flex items-center gap-2 justify-between">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="capitalize">
                  {currentAction?.replace('_', ' ')}
                </Badge>
                <span className="text-sm text-muted-foreground">Result:</span>
              </div>
              {currentAction === 'smart_revision' && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowComparison(true)}
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Compare
                </Button>
              )}
            </div>

            <div className="bg-muted/50 p-4 rounded-lg">
              <Textarea
                value={assistantResult}
                onChange={(e) => setAssistantResult(e.target.value)}
                rows={8}
                className="resize-none border-0 bg-transparent p-0 focus-visible:ring-0"
                placeholder="AI result will appear here..."
              />
            </div>

            <div className="flex gap-2 flex-wrap">
              {(currentAction === 'expand' || currentAction === 'improve' || currentAction === 'generate' || currentAction === 'smart_revision') && (
                <>
                  <Button
                    onClick={handleApplyResult}
                    size="sm"
                    className="flex items-center gap-2"
                  >
                    <CheckCircle className="h-4 w-4" />
                    Apply Changes
                  </Button>
                  {originalContent && (
                    <Button
                      onClick={handleRevert}
                      size="sm"
                      variant="outline"
                      className="flex items-center gap-2"
                    >
                      <RotateCcw className="h-4 w-4" />
                      Revert
                    </Button>
                  )}
                </>
              )}
              
              <Button
                onClick={handleCopyResult}
                size="sm"
                variant="outline"
                className="flex items-center gap-2"
              >
                <Copy className="h-4 w-4" />
                Copy
              </Button>
              
              <Button
                onClick={() => {
                  setAssistantResult("");
                  setCurrentAction(null);
                }}
                size="sm"
                variant="ghost"
              >
                Clear
              </Button>
            </div>
          </div>
        )}

        {/* Custom Prompt Input */}
        <div className="border-t pt-4">
          <CustomPromptInput
            value={customPrompt}
            onChange={setCustomPrompt}
            contentType="note"
            label="Custom Instructions (Optional)"
            placeholder="Add specific instructions to customize AI behavior (e.g., 'Focus on pathophysiology', 'Include case examples')..."
          />
        </div>

        {/* Usage Tips */}
        <div className="text-xs text-muted-foreground bg-muted/30 p-3 rounded">
          <p className="font-medium mb-1">💡 Tips:</p>
          <ul className="space-y-1">
            <li>• Use <strong>Smart Revision</strong> for comprehensive review with comparison view</li>
            <li>• Use <strong>Content Health</strong> to get quality metrics and suggestions</li>
            <li>• Use <strong>Expand</strong> to add more medical details</li>
            <li>• Use <strong>Improve</strong> to enhance clarity and organization</li>
            <li>• Add <strong>Custom Instructions</strong> to guide AI behavior</li>
          </ul>
        </div>

        {/* Revision Templates */}
        <RevisionTemplates
          onApplyTemplate={(prompt) => {
            setCustomPrompt(prompt);
            handleAction('improve');
          }}
          isLoading={isLoading}
        />

        {/* Real-time Suggestions */}
        <RealtimeSuggestions
          content={content}
          enabled={realtimeSuggestionsEnabled}
          onToggle={setRealtimeSuggestionsEnabled}
        />

        {/* Auto-Formatting Tools */}
        <AutoFormatter
          content={content}
          onFormat={onContentUpdate}
          isLoading={isLoading}
        />

        {/* Revision History */}
        {showHistory && (
          <RevisionHistory
            revisions={revisionHistory}
            onRevert={handleRevertToRevision}
            onClose={() => setShowHistory(false)}
          />
        )}

        {/* Revision Comparison Modal */}
        <RevisionComparisonModal
          isOpen={showComparison}
          onClose={() => setShowComparison(false)}
          originalContent={originalContent}
          revisedContent={assistantResult}
          onApply={() => {
            handleApplyResult();
            setShowComparison(false);
          }}
        />
      </CardContent>
    </Card>
  );
});

NoteAIAssistant.displayName = "NoteAIAssistant";

export default NoteAIAssistant;